import { GetStaticPaths, GetStaticProps } from 'next';
import { ArticleJsonLd, BreadcrumbJsonLd, NextSeo } from 'next-seo';
import Image from 'next/image';
import React from 'react';

import { LinkedInButton, TwitterButton } from '../../components/buttons/SocialMediaButtons';
import IntlDate from '../../components/formatters/IntlDate';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import { useGetUrlForCurrentPage } from '../../hooks/useGetUrlForCurrentPage';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiArticle } from '../../lib/integrations/strapi/types';
import { BlogPostRevalidateSeconds } from '../../lib/isr/revalidation';
import { constructHTMLFromMarkdown, decorateMarkdownHtmlRules } from '../../lib/markdown/markdown_utils';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getBlogPostBreadcrumbSeoConfig, getBlogPostJsonSeoConfig, getBlogPostPageSeoConfig } from '../../lib/seo/blogSeoConfigs';
import { StaticMarkdownRenderer } from '../../renderers/StaticMarkdownRenderer';
import { NextPageWithLayout } from '../../types/global-types';
import { def } from '../../utils';

type BlogPostPageStaticProps = {
  article: StrapiApiArticle;
  markdownHtml: string;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const articleUrlPaths = await StrapiApiClient.getAllArticlePaths();
  const paths = articleUrlPaths.map((articleUrlPath) => ({ params: { urlFriendlyName: String(articleUrlPath) } }));
  // 'blocking' means a post published after the last build is server rendered on first request
  // and then cached, instead of 404ing until the site is rebuilt. Crawlers still receive complete HTML.
  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<BlogPostPageStaticProps> = async (context) => {
  const urlFriendlyName = context.params?.urlFriendlyName;
  if (!urlFriendlyName || typeof urlFriendlyName !== 'string') {
    return { notFound: true };
  }
  // Load the article details from the API
  const article = await StrapiApiClient.getArticleByUrlFriendlyName(urlFriendlyName);
  if (!def(article)) {
    return { notFound: true };
  }
  // Load the markdown content and convert it to HTML
  const markdownHtml = await constructHTMLFromMarkdown(article.attributes.content);
  if (!def(markdownHtml)) {
    return { notFound: true };
  }
  // Return the props along with the default props
  const props: BlogPostPageStaticProps = { article, markdownHtml };
  return withGlobalDefaultStaticProps(context, { props, revalidate: BlogPostRevalidateSeconds });
};

const BlogPostPage: NextPageWithLayout<BlogPostPageStaticProps> = (props) => {
  const {
    article: { attributes },
    markdownHtml,
  } = props;
  const currentPageUrl = useGetUrlForCurrentPage();
  const pageSeoConfig = getBlogPostPageSeoConfig(attributes, attributes.tags?.data);
  const articleSeoConfig = getBlogPostJsonSeoConfig(attributes);
  const breadcrumbSeoConfig = getBlogPostBreadcrumbSeoConfig(attributes);
  return (
    <>
      <NextSeo {...pageSeoConfig} />
      <ArticleJsonLd {...articleSeoConfig} />
      <BreadcrumbJsonLd {...breadcrumbSeoConfig} />
      <PageSection isNarrow={true}>
        <p className="font-bold text-purple mb-4 text-2xl">
          <IntlDate date={attributes.publishedAt} />
        </p>
        <div className="flex flex-col items-center justify-items-center mt-4">
          <Image width={1000} height={100} src={attributes.imageUrl} alt={attributes.imageDescription} className="rounded-xl" />
        </div>
        <h1 className="text-6xl my-5">{attributes.title}</h1>
        <div className="flex gap-2  items-center justify-center sm:justify-start mb-4">
          <span className="text-2xl font-bold">Share this article</span>
          <LinkedInButton type="share" url={currentPageUrl} />
          <TwitterButton type="share" url={currentPageUrl} />
        </div>
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <div className="mx-auto max-w-screen-lg">
          <StaticMarkdownRenderer markdownHtml={markdownHtml} decoratorRules={decorateMarkdownHtmlRules} />
        </div>
      </PageSection>
    </>
  );
};
BlogPostPage.displayName = 'BlogPostPage';
export default BlogPostPage;
